import React from 'react';
import { Button, Tooltip, Popover, Paper, Stack, Box, Typography } from '@mui/material';
import { 
  AccountTree as NodeIndexIcon, 
  Delete as DeleteIcon, 
  LocalOffer as TagIcon, 
  Save as SaveIcon, 
  Upload as UploadIcon, 
  Description as FormIcon, 
  Code as CodeIcon, 
  PlayArrow as PlayIcon 
} from '@mui/icons-material';

interface FlowControlsProps {
  onClearAll: () => void;
  onAddNode: () => void;
  onAddStickyNote: (color: string) => void;
  onSave: () => void;
  onUpload: () => void;
  onShowInputs: () => void;
  onShowJson: () => void;
  onExecute: () => void;
  isSaveDisabled: boolean;
  nodeCount: number;
}

const STICKY_NOTE_COLORS = [
  { name: 'Yellow', value: 'yellow', class: 'bg-yellow-500' },
  { name: 'Red', value: 'red', class: 'bg-red-500' },
  { name: 'Blue', value: 'blue', class: 'bg-blue-500' },
  { name: 'Green', value: 'green', class: 'bg-green-500' },
  { name: 'Purple', value: 'purple', class: 'bg-purple-500' },
];

const FlowControls: React.FC<FlowControlsProps> = ({
  onClearAll,
  onAddNode,
  onAddStickyNote,
  onSave,
  onUpload,
  onShowInputs,
  onShowJson,
  onExecute,
  isSaveDisabled,
  nodeCount,
}) => {
  const [colorPickerOpen, setColorPickerOpen] = React.useState(false);

  const colorPickerContent = (
    <Stack direction="row" spacing={1}>
      {STICKY_NOTE_COLORS.map(color => (
        <Tooltip key={color.value} title={color.name}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: color.value,
              border: '2px solid #e5e7eb',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                opacity: 0.8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
              }
            }}
            onClick={() => {
              onAddStickyNote(color.value);
              setColorPickerOpen(false);
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  );

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        right: { xs: 8, md: 32 },
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1202,
        p: 2.5,
        borderRadius: 4,
        bgcolor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 8px 32px rgba(99,102,241,0.10)',
        border: '1px solid #e5e7eb',
        minWidth: 72
      }}
    >
      <Stack direction="column" spacing={2} alignItems="center">
        <Tooltip title={`Add Node (${nodeCount} nodes)`} placement="left">
          <Button 
            variant="outlined"
            onClick={onAddNode}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'primary.main',
              borderColor: 'primary.light',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'primary.50',
                borderColor: 'primary.main',
                boxShadow: '0 2px 8px rgba(99,102,241,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <NodeIndexIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Clear All Nodes" placement="left">
          <Button 
            variant="outlined"
            color="error"
            onClick={onClearAll}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'error.main',
              borderColor: 'error.light',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'error.50',
                borderColor: 'error.main',
                boxShadow: '0 2px 8px rgba(239,68,68,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <DeleteIcon />
          </Button>
        </Tooltip>
        <Popover
          open={colorPickerOpen}
          onClose={() => setColorPickerOpen(false)}
          anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
          PaperProps={{ sx: { p: 2, borderRadius: 2, minWidth: 180 } }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Choose Note Color
          </Typography>
          {colorPickerContent}
        </Popover>
        <Tooltip title="Add Sticky Note" placement="left">
          <Button 
            variant="outlined"
            onClick={() => setColorPickerOpen(true)}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'warning.main',
              borderColor: 'warning.light',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'warning.50',
                borderColor: 'warning.main',
                boxShadow: '0 2px 8px rgba(251,191,36,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <TagIcon />
          </Button>
        </Tooltip>
        <Box sx={{ width: 32, height: 2, bgcolor: 'grey.200', borderRadius: 1, my: 1 }} />
        <Tooltip title={isSaveDisabled ? "Add nodes to save workflow" : "Save Workflow"} placement="left">
          <Button 
            variant="outlined"
            onClick={onSave}
            disabled={isSaveDisabled}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'success.main',
              borderColor: 'success.light',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'success.50',
                borderColor: 'success.main',
                boxShadow: '0 2px 8px rgba(34,197,94,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <SaveIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Upload Workflow" placement="left">
          <Button 
            variant="outlined"
            onClick={onUpload}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'secondary.main',
              borderColor: 'secondary.light',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'secondary.50',
                borderColor: 'secondary.main',
                boxShadow: '0 2px 8px rgba(236,72,153,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <UploadIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Workflow Inputs" placement="left">
          <Button 
            variant="outlined"
            onClick={onShowInputs}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'info.main',
              borderColor: 'info.light',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'info.50',
                borderColor: 'info.main',
                boxShadow: '0 2px 8px rgba(59,130,246,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <FormIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Workflow JSON" placement="left">
          <Button 
            variant="outlined"
            onClick={onShowJson}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'grey.700',
              borderColor: 'grey.300',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'grey.50',
                borderColor: 'grey.700',
                boxShadow: '0 2px 8px rgba(100,116,139,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <CodeIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Execute Workflow" placement="left">
          <Button 
            variant="outlined"
            onClick={onExecute}
            disabled={isSaveDisabled}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: '50%',
              color: 'primary.main',
              borderColor: 'primary.light',
              background: 'white',
              boxShadow: 'none',
              '&:hover': {
                background: 'primary.50',
                borderColor: 'primary.main',
                boxShadow: '0 2px 8px rgba(99,102,241,0.10)',
                transform: 'scale(1.08)'
              },
              transition: 'all 0.2s'
            }}
            size="large"
          >
            <PlayIcon />
          </Button>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

export default FlowControls;